// Starts the walkthroughs (StudioTours.js) and remembers who finished them - on the server (PUT /profile/tours/<name>), so
// a walkthrough doesn't start again on another device. Closing a walkthrough early counts as finished: it can be replayed
// from the avatar menu or Settings → Walkthroughs.
import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { useTour } from '@reactour/tour';
import { finishTour } from '../api/studioAPI';
import { useProfile } from '../context/ProfileContext';
import { adaptStepsForViewport } from './StartTour';
import { TOURS } from './StudioTours';

const StudioTourContext = createContext({ startTour: () => {}, autoStart: () => {}, useTourWhenVisible: () => {} });

export function StudioTourProvider({ children }) {
  const { setIsOpen, setSteps, setCurrentStep, isOpen } = useTour();
  const { profile, setToursDone } = useProfile();
  const running = useRef(null);       // the walkthrough on screen
  const wasOpen = useRef(false);

  const startTour = useCallback((name, context = {}) => {
    if (!TOURS[name] || running.current) return;
    running.current = name;
    setCurrentStep(0);
    setSteps(adaptStepsForViewport(TOURS[name](context)));
    setTimeout(() => setIsOpen(true), 0);
  }, [setCurrentStep, setSteps, setIsOpen]);

  /** Starts it only the first time - never for someone who finished (or closed) it. */
  const autoStart = useCallback((name, context = {}) => {
    if (!profile || (profile.tours_done || []).includes(name)) return;
    startTour(name, context);
  }, [profile, startTour]);

  useEffect(() => {
    if (isOpen) { wasOpen.current = true; return; }
    if (!wasOpen.current || !running.current) return;
    wasOpen.current = false;
    const name = running.current;
    running.current = null;
    setToursDone((done) => Array.from(new Set([...(done || []), name])));
    finishTour(name).catch(() => {});            // only a convenience - a failure just means it may show again
  }, [isOpen, setToursDone]);

  return (
    <StudioTourContext.Provider value={{ startTour, autoStart }}>
      {children}
    </StudioTourContext.Provider>
  );
}

export const useStudioTours = () => useContext(StudioTourContext);

/** Starts a walkthrough the first time `selector` shows up on the page (a screen opening inside another, like a mission's
 * design review), with the context `contextOf()` reads from the page at that moment. */
export function useTourWhenVisible(selector, name, contextOf = () => ({})) {
  const { autoStart } = useStudioTours();
  const { profile } = useProfile();
  const done = (profile?.tours_done || []).includes(name);
  useEffect(() => {
    if (!profile || done) return undefined;
    let timer = null;
    const check = () => {
      if (timer || !document.querySelector(selector)) return;
      timer = setTimeout(() => autoStart(name, contextOf()), 400);      // let the screen settle first
    };
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true });
    check();
    return () => { observer.disconnect(); if (timer) clearTimeout(timer); };
  }, [selector, name, profile, done, autoStart]); // eslint-disable-line react-hooks/exhaustive-deps
}
